// fotosOptimizar.js — P3-9b (preview). Piezas PURAS del preview de optimización
// de fotos existentes: reunir las media únicas de un producto y resumir el ahorro.
// La descarga + reproceso + medición real vive en optimizacionPreview.js (navegador).
// Frontend-only → export ES.

// Reúne las media ÚNICAS de un producto (principal + galería + variaciones),
// deduplicadas por URL (src). Una foto reutilizada aparece UNA vez con todos sus
// usos y el conteo — así el preview la mide una sola vez y la regla «todas o
// ninguna» se ve desde el lado del producto.
// Entrada: { images:[{src,id}], variations:[{image:{src,id}, sku, id}] }.
// → [{ url, id, veces, usos:[texto] }]
export function reunirMediaProducto({ images = [], variations = [] } = {}) {
  const porUrl = new Map();
  const agregar = (src, id, donde) => {
    const key = src == null ? '' : String(src).trim();
    if (!key) return;
    if (!porUrl.has(key)) porUrl.set(key, { url: key, id: id ?? null, veces: 0, usos: [] });
    const m = porUrl.get(key);
    m.veces += 1;
    m.usos.push(donde);
    if (m.id == null && id != null) m.id = id;
  };
  (images || []).forEach((img, i) => agregar(img && img.src, img && img.id, i === 0 ? 'Principal' : `Galería ${i}`));
  (variations || []).forEach((v) => {
    if (v && v.image) {
      // Etiqueta por color/talla de la variación (Mocha, XS); si no hay, el sku/id.
      const opts = (v.attributes || []).map((a) => a && a.option).filter(Boolean).join(', ');
      agregar(v.image.src, v.image.id, opts || `Variación ${(v.sku || v.id || '').toString().trim() || '—'}`);
    }
  });
  return [...porUrl.values()];
}

// Resume los resultados de medición (cada uno con { ok, reprocesar, ahorroBytes }).
// Cuenta cada media UNA vez (ya vienen deduplicadas por url). → totales para el panel.
export function resumirPreview(resultados = []) {
  const medidas = resultados.filter((r) => r && r.ok);
  const aReprocesar = medidas.filter((r) => r.reprocesar);
  const fallidas = resultados.filter((r) => r && r.ok === false);
  const ahorroTotalBytes = aReprocesar.reduce((s, r) => s + (Number(r.ahorroBytes) || 0), 0);
  return {
    total: resultados.length,
    medidas: medidas.length,
    fallidas: fallidas.length,
    aReprocesar: aReprocesar.length,
    ahorroTotalBytes,
  };
}

// P3-9b (pantalla): resumen de una optimización DERIVADO de los estados por foto
// (clave = url → { estado:'lista'|'error'|…, pesoOriginal, pesoNuevo, nombre }). Así
// el resumen queda siempre consistente con las filas, también tras un reintento.
// → { fotos, de, a, ahorroBytes, fallidas, cuales:[nombre] }.
export function resumenOptimizacion(estados = {}) {
  const vals = Object.values(estados || {});
  const listas = vals.filter((e) => e && e.estado === 'lista');
  const errores = vals.filter((e) => e && e.estado === 'error');
  const de = listas.reduce((s, e) => s + (Number(e.pesoOriginal) || 0), 0);
  const a = listas.reduce((s, e) => s + (Number(e.pesoNuevo) || 0), 0);
  return {
    fotos: listas.length,
    de, a, ahorroBytes: Math.max(0, de - a),
    fallidas: errores.length,
    cuales: errores.map((e) => e.nombre || '').filter(Boolean),
  };
}

// Bytes → texto legible (KB/MB) para el panel.
export function pesoLegible(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}
